import { STAY_WINDOW_MS } from '@phidio/protocol';
import { engine } from '../hooks/useEngine.ts';
import { HourglassIcon } from './Icons.tsx';

interface Props {
  peerName: string;
  /** Seconds since the call connected. */
  elapsed: number;
  live: boolean;
  asked: boolean;
  invited: boolean;
  stayed: boolean;
}

/** When we start hinting that this conversation might be worth keeping. */
const NUDGE_AFTER_S = 45;

/**
 * The mutual-stay handshake.
 *
 * Skipping is free, which is exactly why nothing here has weight. "Stay" is the
 * one commitment both people have to make together — and once they have, leaving
 * costs an extra tap.
 */
export function StayPrompt({ peerName, elapsed, live, asked, invited, stayed }: Props) {
  if (!live || stayed) return null;

  if (asked) {
    return (
      <div className="pointer-events-auto w-full max-w-md animate-rise">
        <div className="glass relative overflow-hidden rounded-2xl p-4 shadow-2xl ring-1 ring-amber-300/40">
          <span
            className="countdown-bar absolute inset-x-0 top-0 h-0.5 bg-amber-300"
            style={{ '--countdown': `${STAY_WINDOW_MS}ms` } as React.CSSProperties}
            aria-hidden
          />
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-300/15 text-amber-300">
              <HourglassIcon width={20} height={20} />
            </span>
            <p className="text-sm leading-snug">
              <span className="font-semibold">{peerName}</span> wants to keep talking.
              <span className="block text-white/50">Say yes and neither of you gets skipped.</span>
            </p>
          </div>

          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={() => engine.stay()}
              className="flex-1 rounded-full bg-gradient-to-br from-amber-300 to-amber-500 px-4 py-2.5 text-sm font-bold text-black transition active:scale-95"
            >
              Stay
            </button>
            <button
              type="button"
              onClick={() => engine.next()}
              className="rounded-full bg-white/8 px-4 py-2.5 text-sm font-medium text-white/70 transition hover:bg-white/16"
            >
              Not really
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (invited) {
    return (
      <div className="glass pointer-events-none flex items-center gap-2 rounded-full px-4 py-2 text-xs text-white/60">
        <HourglassIcon width={14} height={14} className="text-amber-300" />
        Waiting for {peerName} to stay too…
      </div>
    );
  }

  if (elapsed < NUDGE_AFTER_S) return null;

  return (
    <button
      type="button"
      onClick={() => engine.stay()}
      className="glass pointer-events-auto flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold text-amber-200 ring-1 ring-amber-300/30 transition active:scale-95 animate-rise"
    >
      <span className="relative flex h-2 w-2">
        <span className="absolute inline-flex h-full w-full rounded-full bg-amber-300 opacity-70 animate-pulse-ring" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-amber-300" />
      </span>
      Going well? Ask {peerName} to stay
    </button>
  );
}
